<script setup>
/**
 * Batch Add Apple Links — parses a CSV (code,link columns) and registers each
 * link via /users/registerCodes. Mirrors Vue2's ManageAppleLinks.vue.
 */
import { ref } from "vue";
import { useRouter } from "vue-router";
import { registerAppleLink } from "./api";

const router = useRouter();
const csvFileName = ref(""), rows = ref([]), loading = ref(false), success = ref(false), error = ref("");

function parseCsv(text) {
  const lines = text.trim().split(/\r?\n/);
  const headers = lines[0].split(",").map(h => h.replace(/^\uFEFF/, "").trim().toLowerCase());
  return lines.slice(1).map(line => {
    const vals = line.split(",");
    return Object.fromEntries(headers.map((h, i) => [h, (vals[i] ?? "").trim()]));
  }).filter(r => r.code);
}

function onFile(e) {
  const file = e.target.files?.[0]; e.target.value = "";
  if (!file) return;
  const reader = new FileReader();
  reader.onload = (ev) => { rows.value = parseCsv(ev.target.result); csvFileName.value = file.name; };
  reader.readAsText(file);
}

async function submit() {
  error.value = ""; success.value = false;
  if (!csvFileName.value) { error.value = "Please include a csv file."; return; }
  loading.value = true;
  try {
    await Promise.all(rows.value.map(r => registerAppleLink({ code: r.code, link: r.link })));
    success.value = true;
  } catch { error.value = "Error occurred. Please try again."; }
  finally { loading.value = false; }
}
</script>

<template>
  <section>
    <VBtn variant="text" prepend-icon="mdi:arrow-left" class="mb-4" @click="router.push({ name: 'users' })">Back</VBtn>
    <h1 class="text-h5 mb-4">Batch Add Apple Links</h1>
    <VCard class="pa-4" max-width="560">
      <VAlert v-if="error" type="error" class="mb-4">{{ error }}</VAlert>
      <VAlert v-if="success" type="success" class="mb-4">Batch add/update successful.</VAlert>
      <div class="mb-4">
        <div class="text-body-2 font-weight-semibold mb-1">CSV File</div>
        <input type="file" accept=".csv" @change="onFile" />
        <p v-if="csvFileName" class="text-caption text-medium-emphasis mt-1">{{ csvFileName }} — {{ rows.length }} row(s)</p>
      </div>
      <VBtn :loading="loading" :disabled="!csvFileName" @click="submit">Submit</VBtn>
    </VCard>
  </section>
</template>
